import { createContext, useContext, useState } from "react";
import type { PropsWithChildren } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import type { IconType } from "@circle-react-shared/Icon/IconNames";
import { NOT_FOUND } from "./constants";

export interface SpaceOption {
  icon?: IconType;
  id: number;
  name: string;
  tooltipText?: string;
  topicsCount?: number;
}
export interface SpaceGroupOption extends SpaceOption {
  spaces: SpaceOption[];
}

export interface SpaceAccessListContextProp {
  addSpaceGroups: (spaceGroupId: number | Array<number>) => void;
  addSpaces: (spaceId: number | Array<number>) => void;
  areAllSpacesAndSpaceGroupsRemovable: () => boolean;
  childFieldName: string;
  fieldArrayFields: {
    spaceGroups: ReturnType<typeof useFieldArray> | Record<string, never>;
    spaces: ReturnType<typeof useFieldArray> | Record<string, never>;
  };
  getHasAnySearchResults: () => boolean;
  getIsFromSearch: (spaceName: string) => boolean;
  hasSpaceGroupUnselectedSpaces: (spaceGroup: SpaceGroupOption) => boolean;
  isSpaceGroupRemovable: (spaceGroupId: number) => boolean;
  isSpaceGroupSelected: (spaceGroupId: number) => boolean;
  isSpaceRemovable: (spaceId: number) => boolean;
  isSpaceSelected: (spaceId: number) => boolean;
  notRemovableChildIds?: Array<number>;
  notRemovableChildItemTooltip?: string;
  notRemovableParentIds?: Array<number>;
  notRemovableParentItemTooltip?: string;
  options: SpaceGroupOption[];
  parentFieldName: string;
  removeSpaceGroup: (spaceGroupId: number) => void;
  removeSpaces: (spaceIds: Array<number>) => void;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;
  spaceGroupsSelected: Array<number>;
  spacesSelected: Array<number>;
}

const SpaceAccessManagementContext =
  createContext<SpaceAccessListContextProp | null>(null);

SpaceAccessManagementContext.displayName = "SpaceAccessManagementContext";

export interface SpaceAccessManagementContextProviderProp {
  childFieldName?: string;
  notRemovableChildIds?: Array<number>;
  notRemovableChildItemTooltip?: string;
  notRemovableParentIds?: Array<number>;
  notRemovableParentItemTooltip?: string;
  options: SpaceGroupOption[];
  parentFieldName?: string;
}

export const SpaceAccessManagementContextProvider = ({
  options,
  children,
  parentFieldName = "space_group_ids",
  childFieldName = "space_ids",
  notRemovableParentItemTooltip,
  notRemovableParentIds,
  notRemovableChildItemTooltip,
  notRemovableChildIds,
}: PropsWithChildren<SpaceAccessManagementContextProviderProp>) => {
  const { watch, setValue } = useFormContext();
  const fieldArrayFields = {
    spaceGroups: useFieldArray({
      name: parentFieldName,
      shouldUnregister: false,
    }),
    spaces: useFieldArray({ name: childFieldName, shouldUnregister: false }),
  };
  const [searchText, setSearchText] = useState("");

  const spaceGroupsSelected = watch(parentFieldName);
  const spacesSelected = watch(childFieldName);

  const removeSpaceGroup = (spaceGroupId: number) => {
    const index = spaceGroupsSelected.findIndex(
      (spaceGroupIdSelected: any) => spaceGroupIdSelected === spaceGroupId,
    );

    if (index !== NOT_FOUND) {
      fieldArrayFields.spaceGroups.remove(index);
    }
  };

  const removeSpaces = (spaceIds: Array<number>) => {
    const removableSpaceIds = spaceIds.filter(isSpaceRemovable);

    setValue(
      childFieldName,
      spacesSelected.filter(
        (spaceId: number) => !removableSpaceIds.includes(spaceId),
      ),
      { shouldDirty: true },
    );
  };

  const addSpaceGroups = (spaceGroupId: number | Array<number>) => {
    fieldArrayFields.spaceGroups.append(spaceGroupId);
  };

  const addSpaces = (spaceId: number | Array<number>) => {
    fieldArrayFields.spaces.append(spaceId);
  };

  const isSpaceGroupSelected = (spaceGroupId: number) =>
    spaceGroupsSelected.includes(spaceGroupId);
  const isSpaceSelected = (spaceId: number) => spacesSelected.includes(spaceId);

  const getIsFromSearch = (name: string) =>
    name.trim().toLowerCase().search(searchText.trim().toLowerCase()) !==
    NOT_FOUND;

  const getHasAnySearchResults = () =>
    options.some(
      spaceGroup =>
        getIsFromSearch(spaceGroup.name) ||
        spaceGroup.spaces.some(space => getIsFromSearch(space.name)),
    );

  const hasSpaceGroupUnselectedSpaces = (spaceGroup: SpaceGroupOption) =>
    spaceGroup.spaces.some(space => !spacesSelected.includes(space.id));

  const isSpaceGroupRemovable = (spaceGroupId: number) =>
    !notRemovableParentIds ||
    notRemovableParentIds.indexOf(spaceGroupId) === NOT_FOUND;
  const isSpaceRemovable = (spaceId: number) =>
    !notRemovableChildIds ||
    notRemovableChildIds.indexOf(spaceId) === NOT_FOUND;
  const areAllSpacesAndSpaceGroupsRemovable = () =>
    (!notRemovableParentIds && !notRemovableChildIds) ||
    (notRemovableParentIds?.length === 0 && notRemovableChildIds?.length === 0);

  const valueToStore = {
    options,
    parentFieldName,
    childFieldName,
    fieldArrayFields,
    spaceGroupsSelected,
    spacesSelected,
    addSpaceGroups,
    addSpaces,
    removeSpaceGroup,
    removeSpaces,
    isSpaceGroupSelected,
    isSpaceSelected,
    searchText,
    setSearchText,
    getIsFromSearch,
    getHasAnySearchResults,
    hasSpaceGroupUnselectedSpaces,
    notRemovableParentItemTooltip,
    notRemovableParentIds,
    notRemovableChildItemTooltip,
    notRemovableChildIds,
    isSpaceGroupRemovable,
    isSpaceRemovable,
    areAllSpacesAndSpaceGroupsRemovable,
  };

  return (
    <SpaceAccessManagementContext.Provider value={valueToStore}>
      {children}
    </SpaceAccessManagementContext.Provider>
  );
};

export const useSpaceAccessManagementContext = () => {
  const context = useContext(SpaceAccessManagementContext);

  if (!context) {
    throw new Error(
      "useSpaceManagementContext must be used within a SpaceManagementContextProvider",
    );
  }
  return context;
};
